import { useCallback, useRef, useState } from 'react'
import isNil from 'lodash/isNil'
import { BillingProfileRecord } from '../../api/dto/client-billing-profile.dto'

const HIGHLIGHT_MS = 1800

/**
 * What is open over the billing-profile table, and the row flash that follows a
 * save. Held apart from the list itself, which only needs the flashed id.
 */
export const useBillingProfileDialogs = () => {
  const [modalRecord, setModalRecord] = useState<BillingProfileRecord | null>(
    null
  )
  const [modalOpen, setModalOpen] = useState(false)
  const [removeRecord, setRemoveRecord] = useState<BillingProfileRecord | null>(
    null
  )
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [uploadModalOpen, setUploadModalOpen] = useState(false)
  const [exportModalOpen, setExportModalOpen] = useState(false)
  const [globalCcOpen, setGlobalCcOpen] = useState(false)
  const [updatedRowId, setUpdatedRowId] = useState<number | null>(null)
  const highlightRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  /** Marks a row as just changed, so a save is visible in the list behind. */
  const flashRow = useCallback((id: number) => {
    if (highlightRef.current) clearTimeout(highlightRef.current)
    setUpdatedRowId(id)
    highlightRef.current = setTimeout(() => setUpdatedRowId(null), HIGHLIGHT_MS)
  }, [])

  const handleOpenCreate = () => {
    setCreateModalOpen(true)
  }

  const handleCloseCreate = (createdId?: number) => {
    setCreateModalOpen(false)
    if (!isNil(createdId)) flashRow(createdId)
  }

  const handleOpenUpload = () => {
    setUploadModalOpen(true)
  }

  const handleCloseUpload = () => {
    setUploadModalOpen(false)
  }

  const handleOpenExport = () => {
    setExportModalOpen(true)
  }

  const handleCloseExport = useCallback(() => {
    setExportModalOpen(false)
  }, [])

  const handleOpenGlobalCc = () => {
    setGlobalCcOpen(true)
  }

  const handleCloseGlobalCc = () => {
    setGlobalCcOpen(false)
  }

  const handleOpenUpdate = useCallback((record: BillingProfileRecord) => {
    setModalRecord(record)
    setModalOpen(true)
  }, [])

  const handleModalClose = (savedId?: number) => {
    setModalOpen(false)
    if (!isNil(savedId)) flashRow(savedId)
    setModalRecord(null)
  }

  const handleOpenRemove = useCallback((record: BillingProfileRecord) => {
    setRemoveRecord(record)
  }, [])

  const handleCloseRemove = useCallback(() => setRemoveRecord(null), [])

  return {
    modalRecord,
    modalOpen,
    removeRecord,
    createModalOpen,
    uploadModalOpen,
    exportModalOpen,
    globalCcOpen,
    updatedRowId,
    flashRow,
    handleOpenCreate,
    handleCloseCreate,
    handleOpenUpload,
    handleCloseUpload,
    handleOpenExport,
    handleCloseExport,
    handleOpenGlobalCc,
    handleCloseGlobalCc,
    handleOpenUpdate,
    handleModalClose,
    handleOpenRemove,
    handleCloseRemove,
  }
}
